import graphql__a15d0e2ea197b8e3eb96 from "./__generated__/CustomerAccountViewRenameCustomerMutation.graphql.ts";
import graphql__94be3bae1079b2786d9e from "./__generated__/CustomerAccountViewUpdateCustomerBusinessAddressMutation.graphql.ts";
import graphql__8c48b19a5892c8005051 from "./__generated__/CustomerAccountViewUpdateCustomerTaxInformationMutation.graphql.ts";
import graphql__2714a3dd24a31112bb2a from "./__generated__/CustomerAccountViewUpdateCustomerTaxStatusMutation.graphql.ts";
import graphql__44c4457a80b1374a3e67 from "./__generated__/CustomerAccountViewUpdateCustomerPreferredLanguageMutation.graphql.ts";
import graphql__e3cf307082d3c7363e29 from "./__generated__/CustomerAccountView_customerAccount.graphql.ts";import * as React from "react";
import {Suspense} from "react";
import {graphql} from "react-relay";
import {useOutletContext} from "react-router";
import {connect, ConnectedProps} from "react-redux";
import {hideModal, showModal} from "../store/actions";
import {useFragment, useRelayEnvironment} from "react-relay/hooks";
import DeleteCustomerDialog from "./dialog/DeleteCustomerDialog";
import {countries, CountryOptions} from "../Countries";
import Panel from "./presentational/Panel";
import EditableDataListItem from "./form/EditableDataListItem";
import * as yup from "yup";
import EditableDataListField, {FieldDataType} from "./form/EditableDataListField";
import ActionButton from "./presentational/button/ActionButton";
import {useErrorContext} from "../contexts/ErrorContext";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";
import {useIntl} from "react-intl";
import ActivityPanel from "./ActivityPanel";

type Props = {}

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type CustomerAccountViewProps = Props & ConnectedProps<typeof connector>;

const getTaxStatuses = (intl) => ({
    null: intl.formatMessage({id: 'customer.taxStatus.unknown'}),
    REGULAR: intl.formatMessage({id: 'customer.taxStatus.regular'}),
    EXEMPT: intl.formatMessage({id: 'customer.taxStatus.exempt'}),
    REVERSE_CHARGE: intl.formatMessage({id: 'customer.taxStatus.reverseCharge'})
});

const getLanguages = (intl) => ({
    de: intl.formatMessage({id: 'language.german'}),
    en: intl.formatMessage({id: 'language.english'}),
});

const handleRenameCustomerSubmission = (input, setEditingActive, relayEnvironment, addError) => {
    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__a15d0e2ea197b8e3eb96,
            variables: {
                input: input
            },
            onCompleted: (response, errors) => {
                setEditingActive(false);
            },
            onError: (error) => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: "errors.failedToUpdateCustomerName"
        }
    )
}

const handleUpdateCustomerBusinessAddress = (input, setEditingActive, relayEnvironment, addError) => {
    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__94be3bae1079b2786d9e,
            variables: {
                input: input
            },
            onCompleted: (response, errors) => {
                setEditingActive(false);
            },
            onError: (error) => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: "errors.failedToUpdateBusinessAddress"
        }
    )
}

const handleUpdateCustomerTaxInformationSubmission = (input, setEditingActive, relayEnvironment, addError) => {
    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__8c48b19a5892c8005051,
            variables: {
                input: input
            },
            onCompleted: (response, errors) => {
                setEditingActive(false);
            },
            onError: (error) => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: "errors.failedToUpdateVatId"
        }
    )
}

const handleUpdateCustomerTaxStatusSubmission = (input, setEditingActive, relayEnvironment, addError) => {
    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__2714a3dd24a31112bb2a,
            variables: {
                input: input
            },
            onCompleted: (response, errors) => {
                setEditingActive(false);
            },
            onError: (error) => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: "errors.failedToUpdateTaxStatus"
        }
    )
}

const handleUpdatePreferredLanguageSubmission = (input, setEditingActive, relayEnvironment, addError) => {
    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__44c4457a80b1374a3e67,
            variables: {
                input: input
            },
            onCompleted: (response, errors) => {
                setEditingActive(false);
            },
            onError: (error) => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: "errors.failedToUpdatePreferredLanguage"
        }
    )
}

const CustomerAccountView: React.FC<CustomerAccountViewProps> = (props) => {
    const intl = useIntl();
    const queryRef: any = useOutletContext();
    const {addError} = useErrorContext();
    const customerAccount = useFragment(
        graphql__e3cf307082d3c7363e29,
        queryRef
    );

    const relayEnvironment = useRelayEnvironment();

    const {dispatchShowModal, dispatchHideModal} = props;

    const showDeleteCustomerAccountDialog = () => {
        dispatchShowModal({
            body: (<DeleteCustomerDialog queryRef={customerAccount} handleCloseDialog={dispatchHideModal}/>)
        })
    };

    const taxStatuses = getTaxStatuses(intl);
    const languages = getLanguages(intl);

    const customerNameValidationSchema = yup.object().shape({
        customerName: yup.string()
            .min(2, intl.formatMessage({id: 'validation.field.customerNameTooShort'}))
            .max(255, intl.formatMessage({id: 'validation.field.customerNameTooLong'}))
            .required(intl.formatMessage({id: 'validation.field.required'}))
    });

    const customerTaxInformationValidationSchema = yup.object().shape({
        vatId: yup.string()
            .min(2, intl.formatMessage({id: 'validation.field.vatIdTooShort'}))
            .max(23, intl.formatMessage({id: 'validation.field.vatIdTooLong'}))
            .matches(/^[A-Z0-9,.\- ]{2,23}$/, intl.formatMessage({id: 'validation.field.vatIdInvalid'}))
            .nullable()
    });

    const validTaxStatusKeys = Object.keys(taxStatuses).filter(key => key !== 'null');
    const customerTaxStatusValidationSchema = yup.object().shape({
        taxStatus: yup.string()
            .oneOf(validTaxStatusKeys, intl.formatMessage({id: 'validation.field.invalidTaxStatus'}))
    });

    const businessAddressValidationSchema = yup.object().shape({
        businessName: yup.string()
            .min(2, intl.formatMessage({id: 'validation.field.businessNameTooShort'}))
            .max(255, intl.formatMessage({id: 'validation.field.businessNameTooLong'}))
            .nullable()
            .required(intl.formatMessage({id: 'validation.field.required'})),
        postalCode: yup.string()
            .matches(/^[a-zA-Z0-9 .-]{2,12}$/, intl.formatMessage({id: 'validation.field.postalCodeInvalid'}))
            .nullable()
            .required(intl.formatMessage({id: 'validation.field.required'})),
        city: yup.string()
            .matches(/^[-+&\p{L}\p{P}\d .]{2,255}$/u, intl.formatMessage({id: 'validation.field.cityInvalid'}))
            .nullable()
            .required(intl.formatMessage({id: 'validation.field.required'})),
    });

    return (
        <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                    <Panel headline={intl.formatMessage({id: 'view.customerAccount.headline'})}>
                        <dl className="divide-y divide-gray-200">
                            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                                <dt className="text-sm font-medium text-gray-500">{intl.formatMessage({id: 'customer.field.customerNumber'})}</dt>
                                <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    <span className="grow">{customerAccount.customerNumber}</span>
                                </dd>
                            </div>

                            <EditableDataListItem
                                label={intl.formatMessage({id: 'customer.field.customerName'})}
                                fields={(<EditableDataListField type="text" name="customerName" placeholder={intl.formatMessage({id: 'customer.field.customerName.placeholder'})} autoFocus={true}/>)}
                                initialValues={{id: customerAccount.id, customerName: customerAccount.customerName}}
                                validationSchema={customerNameValidationSchema}
                                handleSubmission={(values, setEditingActive) => handleRenameCustomerSubmission(values, setEditingActive, relayEnvironment, addError)}
                            >
                                {customerAccount.customerName}
                            </EditableDataListItem>

                            <EditableDataListItem
                                label={intl.formatMessage({id: 'customer.field.businessAddress'})}
                                fields={(
                                    <>
                                        <EditableDataListField type="text" name="businessName" autoFocus={true}/>
                                        <EditableDataListField type="text" name="addressLine1" placeholder={intl.formatMessage({id: 'customer.field.addressLine1.placeholder'})}/>
                                        <EditableDataListField type="text" name="addressLine2" placeholder={intl.formatMessage({id: 'customer.field.addressLine2.placeholder'})}/>
                                        <EditableDataListField type="text" name="postalCode" placeholder={intl.formatMessage({id: 'customer.field.postalCode.placeholder'})}/>
                                        <EditableDataListField type="text" name="city" placeholder={intl.formatMessage({id: 'customer.field.city.placeholder'})}/>
                                        <EditableDataListField name="country" fieldDataType={FieldDataType.select}>
                                            <CountryOptions/>
                                        </EditableDataListField>
                                    </>
                                )}
                                initialValues={{
                                    id: customerAccount.id,
                                    businessName: customerAccount.businessAddress ? customerAccount.businessAddress.businessName : null,
                                    addressLine1: customerAccount.businessAddress ? customerAccount.businessAddress.addressLine1 : null,
                                    addressLine2: customerAccount.businessAddress ? customerAccount.businessAddress.addressLine2 : null,
                                    postalCode: customerAccount.businessAddress ? customerAccount.businessAddress.postalCode : null,
                                    city: customerAccount.businessAddress ? customerAccount.businessAddress.city : null,
                                    country: customerAccount.businessAddress ? customerAccount.businessAddress.country : 'DEU'
                                }}
                                validationSchema={businessAddressValidationSchema}
                                handleSubmission={(values, setEditingActive) => handleUpdateCustomerBusinessAddress(values, setEditingActive, relayEnvironment, addError)}
                            >
                                {customerAccount.businessAddress && <>
                                    {customerAccount.businessAddress.businessName}<br/>
                                    {customerAccount.businessAddress.addressLine1}<br/>
                                    {customerAccount.businessAddress.addressLine2}{customerAccount.businessAddress.addressLine2 && <br/>}
                                    {customerAccount.businessAddress.postalCode} {customerAccount.businessAddress.city}<br/>
                                    {countries[customerAccount.businessAddress.country]}
                                </>}
                            </EditableDataListItem>

                            <EditableDataListItem
                                label={intl.formatMessage({id: 'customer.field.vatId'})}
                                fields={(<EditableDataListField type="text" name="vatId" placeholder={intl.formatMessage({id: 'customer.field.vatId.placeholder'})} autoFocus={true}/>)}
                                initialValues={{id: customerAccount.id, vatId: customerAccount.vatId}}
                                validationSchema={customerTaxInformationValidationSchema}
                                handleSubmission={(values, setEditingActive) => handleUpdateCustomerTaxInformationSubmission(values, setEditingActive, relayEnvironment, addError)}
                            >
                                {customerAccount.vatId}
                            </EditableDataListItem>

                            <EditableDataListItem
                                label={intl.formatMessage({id: 'customer.field.taxStatus'})}
                                fields={(
                                    <>
                                        <EditableDataListField name="taxStatus" fieldDataType={FieldDataType.select}>
                                            {Object.keys(taxStatuses).map((key) => {
                                                return (
                                                    <option key={key} value={key}>{taxStatuses[key]}</option>
                                                )
                                            })}
                                        </EditableDataListField>
                                    </>
                                )}
                                initialValues={{
                                    id: customerAccount.id,
                                    taxStatus: customerAccount.taxStatus ?? null
                                }}
                                validationSchema={customerTaxStatusValidationSchema}
                                handleSubmission={(values, setEditingActive) => handleUpdateCustomerTaxStatusSubmission(values, setEditingActive, relayEnvironment, addError)}
                            >
                                {taxStatuses[customerAccount.taxStatus]}
                            </EditableDataListItem>

                            <EditableDataListItem
                                label={intl.formatMessage({id: 'customer.field.preferredLanguage'})}
                                fields={(
                                    <>
                                        <EditableDataListField name="preferredLanguage" fieldDataType={FieldDataType.select}>
                                            {Object.keys(languages).map((key) => {
                                                return (
                                                    <option key={key} value={key}>{languages[key]}</option>
                                                )
                                            })}
                                        </EditableDataListField>
                                    </>
                                )}
                                initialValues={{
                                    id: customerAccount.id,
                                    preferredLanguage: customerAccount.preferredLanguage
                                }}
                                handleSubmission={(values, setEditingActive) => handleUpdatePreferredLanguageSubmission(values, setEditingActive, relayEnvironment, addError)}
                            >
                                {languages[customerAccount.preferredLanguage]}
                            </EditableDataListItem>
                        </dl>
                    </Panel>
                </div>
                <div>
                    <Suspense fallback={<Panel headline={intl.formatMessage({id: 'activity.recentActivity', defaultMessage: 'Recent Activity'})}><div className="py-8 text-center text-gray-400">Loading...</div></Panel>}>
                        <ActivityPanel customerAccountId={customerAccount.customerAccountIdentifier} customerAccountGraphqlId={customerAccount.id} />
                    </Suspense>
                </div>
            </div>
            <div className="flex space-x-4 justify-end mt-6">
                <ActionButton onClick={showDeleteCustomerAccountDialog} label={intl.formatMessage({id: 'button.deleteAccount'})}/>
            </div>
        </>
    )
}

export default connector(CustomerAccountView);
